// PartiesOnBallot.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import useBallotParties from '../../../hooks/useBallotParties';
import { partyColor, partyName, partyPath, NO_PARTY_COLOR } from '../../../utils/parties';
import './Parties.css';

/**
 * PartiesOnBallot
 *
 * Every party label on a state's next ballot, largest first, each one a way
 * into that party's page. Sits at the top of the elections panel, on the
 * candidates page, and at the top of a party page as the way across to the
 * others on the same ballot (activeKey marks the one being read).
 *
 * Many readers arrive knowing their party before they know a single name on
 * the ballot, so this puts the party one tap from the home page rather than
 * behind a candidate.
 *
 * The bar under the chips is the ballot's make-up by candidate count. It is
 * the share of this ballot's candidates each label fielded and nothing more --
 * not strength, not polling -- which is why it has no numbers of its own: the
 * counts are on the chips.
 *
 * Labels that name no party (Independent, Write-In) are shown, not dropped.
 * They are part of the ballot, and a strip that left them out would read as
 * though every candidate had a party. They are not links, because there is no
 * party page for them to open; the API's own reason is their tooltip.
 *
 * Fails silently. The strip is additive to the panels it sits in, and an
 * unanswered ballot costs the strip and nothing else.
 */

function PartiesOnBallot({ stateAbbr, activeKey = null, showHint = false, className = '' }) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { data, loading, error } = useBallotParties(stateAbbr);

  if (!stateAbbr || error) return null;

  if (loading) {
    return (
      <div className={`ballot-parties ${className}`} aria-busy="true">
        <div className="ballot-parties-row">
          {[0, 1, 2].map((i) => (
            <span key={i} className="ballot-party ballot-party--skeleton" />
          ))}
        </div>
      </div>
    );
  }

  const parties = data?.parties || [];
  if (!parties.length) return null;

  const total = parties.reduce((sum, row) => sum + (Number(row.candidates) || 0), 0);

  return (
    <section
      className={`ballot-parties ${className}`}
      aria-label={t('common:partiesOnBallot', 'Parties on the ballot')}
    >
      <div className="ballot-parties-head">
        <span className="ballot-parties-title">
          {t('common:partiesOnBallot', 'Parties on the ballot')}
        </span>
        {showHint && (
          <span className="ballot-parties-hint">
            {t('common:partiesOnBallotHint', 'Tap one for its platform, news and money')}
          </span>
        )}
      </div>

      <div className="ballot-parties-row" role="list">
        {parties.map((row) => {
          const key = row.party_key || null;
          const count = Number(row.candidates) || 0;
          const color = key ? partyColor(key) : NO_PARTY_COLOR;
          const countLabel = t('common:candidateCount', '{{count}} candidates', { count });

          if (!key) {
            return (
              <span
                key={`label-${row.label}`}
                role="listitem"
                className="ballot-party ballot-party--plain"
                title={row.why_no_party || undefined}
              >
                <span className="party-badge-dot" style={{ backgroundColor: color }} aria-hidden />
                <span className="ballot-party-label">{row.label}</span>
                <span className="ballot-party-count" aria-label={countLabel}>
                  {count}
                </span>
              </span>
            );
          }

          const active = key === activeKey;
          return (
            <button
              key={key}
              type="button"
              role="listitem"
              className={`ballot-party ${active ? 'ballot-party--active' : ''}`}
              style={{ '--party-color': color }}
              aria-current={active ? 'page' : undefined}
              aria-label={`${partyName(key)}, ${countLabel}`}
              onClick={() => {
                if (!active) navigate(partyPath(key, stateAbbr));
              }}
            >
              <span className="party-badge-dot" style={{ backgroundColor: color }} aria-hidden />
              <span className="ballot-party-label">{row.label}</span>
              <span className="ballot-party-count">{count}</span>
            </button>
          );
        })}
      </div>

      {total > 0 && (
        <div className="ballot-parties-bar" aria-hidden>
          {parties.map((row) => {
            const count = Number(row.candidates) || 0;
            if (!count) return null;
            return (
              <span
                key={`bar-${row.party_key || row.label}`}
                style={{
                  flexGrow: count,
                  backgroundColor: row.party_key ? partyColor(row.party_key) : NO_PARTY_COLOR,
                  opacity: activeKey && row.party_key !== activeKey ? 0.35 : 1,
                }}
              />
            );
          })}
        </div>
      )}
    </section>
  );
}

export default PartiesOnBallot;